"use client"

import {
  ArrowRight,
  BookOpenCheck,
  Database,
  Factory,
  Image as ImageIcon,
  Loader2,
  Menu,
  Mic,
  PenTool,
  Send,
  SquareFunction,
  Sparkles,
  Trash2,
  X,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useCookies } from "react-cookie"
import { useModal } from "../modal/Provdier"
import { useLogin, useUserInfo } from "@/hooks/useUser"
import { useVoice } from "@/hooks/useVoice"
import { filterKnowledgeByTenant } from "@/constants/knowledge"
import { filterVisibleModels } from "@/constants/model"
import VoiceWave from "./VoiceWave"
import type { IBus, IData } from "@/types"

const ASSET_BASE = "/images/huizhi"
const HUIZHI_TENANT_ID = "huizhi"
const WK_API_BASE = "/wk-api"
const WKJG_API_BASE = "/wk-api"
const HUIZHI_CHAT_PATH = "/chat/teacher-chat"
const TEACHER_CHAT_PAYLOAD_COOKIE = "teacherChatPayload"
// TODO(agent-scope): agentId 来源待定，当前临时使用固定 AgentScope 智能体跑通联调流程。
const TEMP_AGENT_SCOPE_AGENT_ID = "2062789053285715969"
const ENERGY_AGENT_SCOPE_AGENT_ID = "2064648682564431874"
const ENERGY_MODEL_PROFILE_ID = "__energy_model__"
const MAX_UPLOAD_COUNT = 3
const MAX_UPLOAD_SIZE = 30 * 1024 * 1024

interface UploadedFile {
  fileId: string
  fileName: string
  fileType: string
  fileUrl?: string
}

interface ModelCatalogItem {
  agentId?: string
  displayName?: string
  enabled?: boolean
  modelName?: string
  modelProfileId: string
  providerType?: string
}

const ENERGY_MODEL_OPTION: ModelCatalogItem = {
  agentId: ENERGY_AGENT_SCOPE_AGENT_ID,
  displayName: "能源大模型",
  enabled: true,
  modelName: "energy-large-model",
  modelProfileId: ENERGY_MODEL_PROFILE_ID,
}

interface KnowledgeOption {
  displayName: string
  knowledgeKey: string
}

function getGreeting() {
  const hour = new Date().getHours()
  if (hour < 12) return "早上好"
  if (hour < 18) return "下午好"
  return "晚上好"
}

function buildChatUrl(params: Record<string, string>) {
  const url = new URL(HUIZHI_CHAT_PATH, window.location.origin)
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })
  return url.toString()
}

function getFileType(file: File, uploaded?: Record<string, any>) {
  return (
    uploaded?.fileType?.toString?.() ||
    uploaded?.type?.toString?.() ||
    file.type ||
    file.name.split(".").pop() ||
    "file"
  )
}

function normalizeUploadedFile(file: File, uploaded: Record<string, any>): UploadedFile {
  return {
    fileId: (uploaded?.fileId || uploaded?.id || uploaded?.filePath || uploaded?.fileUrl || "").toString(),
    fileName: uploaded?.fileName || uploaded?.name || file.name,
    fileType: getFileType(file, uploaded),
    fileUrl: uploaded?.fileUrl || uploaded?.url || uploaded?.filePath,
  }
}

function writeTeacherChatPayload(payload: {
  question: string
  model: string
  modelProfileId: string
  kb: string
  activeKnowledgeKeys: string[]
  files: UploadedFile[]
}) {
  document.cookie = `${TEACHER_CHAT_PAYLOAD_COOKIE}=${encodeURIComponent(
    JSON.stringify(payload)
  )}; path=/; max-age=300; SameSite=Lax`
}

function HuizhiHeader() {
  const { setIsLoginOpen, setIsRegisterOpen } = useModal()
  const [userInfo] = useUserInfo()
  const { logout } = useLogin()
  const [cookies, setCookie, removeCookie] = useCookies(["token", "healthToken"])
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const tokenHead = cookies.token?.tokenHead || ""
  const tokenValue = cookies.token?.token || ""
  const healthTokenRequestRef = useRef("")
  const authorization = useMemo(() => {
    return tokenValue ? `${tokenHead}${tokenValue}` : ""
  }, [tokenHead, tokenValue])

  useEffect(() => {
    let alive = true
    if (!authorization) return
    if (healthTokenRequestRef.current === authorization) return
    healthTokenRequestRef.current = authorization

    async function loadHealthToken() {
      try {
        const response = await fetch(`${WKJG_API_BASE}/wenkoo-tenant/user/getHealthToken`, {
          method: "GET",
          headers: {
            Authorization: authorization,
            "tenant-id": "huizhi",
          },
        })
        const result = await response.json()
        if (!alive || !response.ok || result.code !== 0 || !result.data) return
        if (cookies.healthToken === result.data) return
        setCookie("healthToken", result.data, {
          path: "/",
          maxAge: 24 * 60 * 60,
          secure: true,
          sameSite: "none",
        })
      } catch {
        if (alive) {
          removeCookie("healthToken", { path: "/" })
        }
      }
    }

    loadHealthToken()

    return () => {
      alive = false
    }
  }, [authorization, cookies.healthToken, removeCookie, setCookie])

  function openLogin() {
    setIsMenuOpen(false)
    setIsLoginOpen(true)
  }

  function openRegister() {
    setIsMenuOpen(false)
    setIsRegisterOpen(true)
  }

  function handleLogout() {
    removeCookie("healthToken", { path: "/" })
    logout()
  }

  return (
    <header className="sticky top-0 z-50 bg-white/70 px-4 py-3 backdrop-blur-sm md:px-8 md:py-4">
      <div className="flex items-center justify-between gap-4">
        <a href="#" className="flex min-w-0 items-center" aria-label="汇知AI首页">
          <img
            src={`${ASSET_BASE}/huizhi/landingpage.png`}
            alt="汇知AI"
            className="h-10 max-w-[260px] object-contain md:h-12 md:max-w-[420px]"
          />
        </a>

        <div className="hidden items-center gap-6 whitespace-nowrap text-sm font-bold xl:flex">
          {userInfo?.id ? (
            <div className="group relative">
              <button
                type="button"
                className="flex h-10 items-center gap-2 rounded-full bg-white px-3 text-sm font-bold text-[#303133] shadow-sm ring-1 ring-gray-100 transition-colors hover:text-[#0F60FF]"
              >
                <span className="flex size-7 items-center justify-center rounded-full bg-[#0F60FF] text-xs text-white">
                  {(userInfo.nickName || userInfo.phone || "用").slice(0, 1)}
                </span>
                <span className="max-w-[120px] truncate">{userInfo.phone || userInfo.nickName}</span>
              </button>
              <div className="absolute right-0 top-full hidden min-w-[220px] pt-3 group-hover:block">
                <div className="overflow-hidden rounded-xl bg-white text-sm shadow-[0_12px_30px_-18px_rgba(0,0,0,0.35)] ring-1 ring-gray-100">
                  <div className="bg-blue-50 px-4 py-3 text-[#303133]">
                    <div className="font-bold">汇知AI用户</div>
                    <div className="mt-1 truncate text-xs">{userInfo.phone}</div>
                  </div>
                  <div className="p-2">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full rounded-lg bg-gray-50 px-3 py-2 text-left font-bold text-[#303133] transition-colors hover:bg-gray-100"
                    >
                      退出登录
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={openLogin}
                className="font-bold text-gray-600 transition-colors hover:text-[#0F60FF]"
              >
                登录
              </button>
              <button
                type="button"
                onClick={openRegister}
                className="rounded-full bg-[#0F60FF] px-4 py-1.5 font-bold text-white shadow-sm transition-colors hover:bg-blue-600"
              >
                注册
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setIsMenuOpen((open) => !open)}
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-[#141413] shadow-sm ring-1 ring-gray-200 transition-colors hover:text-[#0F60FF] xl:hidden"
          aria-label={isMenuOpen ? "关闭菜单" : "打开菜单"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {isMenuOpen && (
        <div className="mt-3 rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_16px_40px_-24px_rgba(15,96,255,0.45)] xl:hidden">
          {userInfo?.id ? (
            <div>
              <div className="mb-2 rounded-xl bg-blue-50 px-3 py-2 text-sm text-[#303133]">
                <div className="font-bold">汇知AI用户</div>
                <div className="truncate text-xs">{userInfo.phone || userInfo.nickName}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false)
                  handleLogout()
                }}
                className="w-full rounded-full bg-gray-100 px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-200"
              >
                退出登录
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={openLogin}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:border-[#0F60FF] hover:text-[#0F60FF]"
              >
                登录
              </button>
              <button
                type="button"
                onClick={openRegister}
                className="rounded-full bg-[#0F60FF] px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-blue-600"
              >
                注册
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  )
}

function SearchBox({ isExpired }: { isExpired: boolean }) {
  const { openModal, setIsLoginOpen } = useModal()
  const [cookies] = useCookies(["token"])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [query, setQuery] = useState("")

  // 语音识别完成后, 将文本追加到输入框
  const handleVoiceDone = useCallback((text: string) => {
    setQuery((prev) => (prev ? `${prev}${text}` : text))
  }, [])
  const { status: voiceStatus, toggleRecord } = useVoice(handleVoiceDone)

  // 点击语音按钮: 未登录弹出登录框, 已登录开始/结束录音
  function handleVoiceClick() {
    if (!cookies.token) {
      setIsLoginOpen(true)
      return
    }
    toggleRecord()
  }

  const [model, setModel] = useState("")
  const [kb, setKb] = useState("")
  const [modelOptions, setModelOptions] = useState<ModelCatalogItem[]>([])
  const [knowledgeOptions, setKnowledgeOptions] = useState<KnowledgeOption[]>([])
  const [greeting, setGreeting] = useState(getGreeting)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState("")
  const tokenHead = cookies.token?.tokenHead || ""
  const tokenValue = cookies.token?.token || ""
  const authorization = useMemo(() => {
    return tokenValue ? `${tokenHead}${tokenValue}` : ""
  }, [tokenHead, tokenValue])
  const selectedAgentId = useMemo(() => {
    const selectedModel = modelOptions.find((item) => String(item.modelProfileId) === String(model))
    return selectedModel?.agentId || TEMP_AGENT_SCOPE_AGENT_ID
  }, [model, modelOptions])

  function getAuthorization() {
    return authorization
  }

  useEffect(() => {
    setGreeting(getGreeting())
  }, [])

  useEffect(() => {
    let alive = true
    const headers = {
      ...(authorization ? { Authorization: authorization } : {}),
      "Tenant-ID": HUIZHI_TENANT_ID,
    }

    async function loadModelCatalog() {
      try {
        const response = await fetch(`${WK_API_BASE}/wenkoo-portal/agentScope/chat/models`, {
          headers,
        })
        const result = await response.json()
        if (!response.ok || result.code !== 0) {
          if (alive) {
            setModelOptions([ENERGY_MODEL_OPTION])
            setModel((current) => current || ENERGY_MODEL_PROFILE_ID)
          }
          return
        }
        const items = Array.isArray(result.data?.items)
          ? filterVisibleModels<ModelCatalogItem>(
            result.data.items.filter(
              (item: ModelCatalogItem) => item?.modelProfileId !== undefined && item?.enabled !== false,
            ),
          )
          : []
        const options = [...items, ENERGY_MODEL_OPTION]
        if (!alive) return
        setModelOptions(options)
        if (options[0]?.modelProfileId !== undefined) {
          setModel((current) => current || String(options[0].modelProfileId))
        }
      } catch {
        if (alive) {
          setModelOptions([ENERGY_MODEL_OPTION])
          setModel((current) => current || ENERGY_MODEL_PROFILE_ID)
        }
      }
    }

    loadModelCatalog()

    return () => {
      alive = false
    }
  }, [authorization])

  useEffect(() => {
    let alive = true
    const headers = {
      ...(authorization ? { Authorization: authorization } : {}),
      "Tenant-ID": HUIZHI_TENANT_ID,
    }

    async function loadAgentRuntime() {
      try {
        const query = new URLSearchParams({ agentId: selectedAgentId })
        const response = await fetch(`${WK_API_BASE}/wenkoo-portal/agentScope/chat/agent/runtime?${query.toString()}`, {
          headers,
        })
        const result = await response.json()
        if (!response.ok || result.code !== 0) return
        const availableItems: KnowledgeOption[] = Array.isArray(result.data?.knowledge)
          ? result.data.knowledge
            .filter(
              (item: any) => item?.available !== false && item?.enabled !== false && item?.catalog?.enabled !== false,
            )
            .map((item: any) => ({
              displayName:
                item?.catalog?.displayName || item?.knowledgeKey || item?.catalog?.knowledgeKey || "知识库",
              knowledgeKey: item?.knowledgeKey || item?.catalog?.knowledgeKey || "",
            }))
            .filter((item: KnowledgeOption) => item.knowledgeKey)
          : []
        // 按机构白名单展示知识库
        const items = filterKnowledgeByTenant(availableItems, HUIZHI_TENANT_ID)
        if (!alive) return
        setKnowledgeOptions(items)
        setKb((current) => (items.some((item) => item.knowledgeKey === current) ? current : ""))
      } catch {
        if (alive) setKnowledgeOptions([])
      }
    }

    loadAgentRuntime()

    return () => {
      alive = false
    }
  }, [authorization, selectedAgentId])

  const chips = useMemo(
    () => [
      "什么是辐射管？",
      "Hypol工艺和Spheripol工艺的区别是什么？",
      "介绍聚氯乙烯的生产工艺和氯乙单体悬浮聚合典型配方",
      "煤层孔隙结构具有哪两种类型？",
      "原料制油(XTL)的间接液化完整工艺通常分为哪3个工序？",
      "光合作用光反应的场所是什么？",
      "纯净物和混合物的根本区别是什么？",
      "实验室制取二氧化碳的药品是什么？",
    ],
    []
  )

  async function uploadFile(file: File) {
    const authorization = getAuthorization()
    const formData = new FormData()
    formData.append("file", file)
    formData.append("fileType", "7")
    formData.append("fileFolderType", "8")

    const response = await fetch("/wk-api/wenkoo-portal/aiChat/chatUploadFile", {
      method: "POST",
      headers: {
        Authorization: authorization,
        "Tenant-ID": "wkjg",
      },
      body: formData,
    })
    const result = await response.json()
    if (!response.ok || result.code !== 0 || !result.data) {
      throw new Error(result.msg || result.message || "上传失败")
    }
    return normalizeUploadedFile(file, result.data)
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = Array.from(event.target.files || [])
    event.target.value = ""
    if (!selectedFiles.length) return

    setUploadError("")
    if (!cookies.token) {
      setIsLoginOpen(true)
      return
    }

    if (uploadedFiles.length + selectedFiles.length > MAX_UPLOAD_COUNT) {
      setUploadError(`最多上传 ${MAX_UPLOAD_COUNT} 个文件`)
      return
    }

    const oversizedFile = selectedFiles.find((file) => file.size > MAX_UPLOAD_SIZE)
    if (oversizedFile) {
      setUploadError(`${oversizedFile.name} 超过 30MB`)
      return
    }

    setUploading(true)
    try {
      const files = await Promise.all(selectedFiles.map(uploadFile))
      setUploadedFiles((current) => [...current, ...files])
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "上传失败")
    } finally {
      setUploading(false)
    }
  }

  function handleSearch() {
    if (!query.trim() && !uploadedFiles.length) return
    if (!cookies.token) {
      setIsLoginOpen(true)
      return
    }
    if (isExpired) {
      openModal()
      return
    }
    writeTeacherChatPayload({
      question: query.trim(),
      model,
      modelProfileId: model,
      kb,
      activeKnowledgeKeys: kb ? [kb] : [],
      files: uploadedFiles,
    })
    window.open(
      buildChatUrl({
        q: query.trim(),
        modelProfileId: model,
        knowledgeKey: kb,
      }),
      "_blank"
    )
  }

  return (
    <section className="mx-auto mb-20 mt-16 flex w-full max-w-4xl flex-col items-center justify-center px-4">
      <div className="mb-10 text-center">
        <h1 className="mb-4 text-4xl font-bold text-[#141413] md:text-5xl">
          {greeting}，我是汇知AI
        </h1>
        <p className="font-serif text-lg text-gray-600">
          为您提供专业、精准、可溯源的知识服务
        </p>
      </div>

      <div className="w-full rounded-[22px] border border-gray-100 bg-white p-2 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)]">
        <div className="flex flex-col">
          <textarea
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault()
                handleSearch()
              }
            }}
            placeholder="输入您的问题，或者上传图片..."
            className="min-h-[120px] w-full resize-none bg-transparent p-4 font-serif text-lg focus:outline-none"
          />

          <div className="mt-2 flex flex-col gap-3 border-t border-gray-50 p-2 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
                accept=".pdf,.xlsx,.xls,.doc,.docx,.txt,.png,.jpeg,.jpg,.ppt,.pptx"
              />
              <button
                type="button"
                onClick={() => {
                  if (!cookies.token) {
                    setIsLoginOpen(true)
                    return
                  }
                  fileInputRef.current?.click()
                }}
                disabled={uploading || uploadedFiles.length >= MAX_UPLOAD_COUNT}
                className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="上传文件"
              >
                <ImageIcon size={20} />
              </button>

              <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5">
                <Database size={16} className="text-[#0F60FF]" />
                <select
                  value={kb}
                  onChange={(event) => setKb(event.target.value)}
                  className="cursor-pointer bg-transparent text-sm focus:outline-none"
                >
                  <option value="">不参考知识库</option>
                  {knowledgeOptions.map((item) => (
                    <option key={item.knowledgeKey} value={item.knowledgeKey}>
                      {item.displayName}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5">
                <Sparkles size={16} className="text-[#d97757]" />
                <select
                  value={model}
                  onChange={(event) => setModel(event.target.value)}
                  className="cursor-pointer bg-transparent text-sm focus:outline-none"
                >
                  {modelOptions.length ? (
                    modelOptions.map((item) => (
                      <option key={item.modelProfileId} value={String(item.modelProfileId)}>
                        {item.displayName || item.modelName || item.providerType || item.modelProfileId}
                      </option>
                    ))
                  ) : (
                    <option value="">模型加载中</option>
                  )}
                </select>
              </label>
            </div>

            <div className="flex items-center gap-2 self-end md:self-auto">
              <button
                type="button"
                onClick={handleVoiceClick}
                className={`rounded-xl p-3 transition-all ${voiceStatus === "idle"
                  ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  : "bg-[#0F60FF] text-white shadow-lg shadow-blue-500/30"
                  }`}
                aria-label={voiceStatus === "recording" ? "结束录音" : "语音输入"}
              >
                {voiceStatus === "loading" ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : voiceStatus === "recording" ? (
                  <VoiceWave className="text-white" />
                ) : (
                  <Mic size={20} />
                )}
              </button>

              <button
                type="button"
                onClick={handleSearch}
                className={`rounded-xl p-3 transition-all ${query.trim() || uploadedFiles.length
                  ? "bg-[#0F60FF] text-white shadow-lg shadow-blue-500/30"
                  : "bg-gray-100 text-gray-400"
                  }`}
                aria-label="发送"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {(uploadedFiles.length > 0 || uploadError || uploading) && (
        <div className="mt-3 w-full rounded-2xl border border-gray-100 bg-white/80 p-3 text-sm shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            {uploadedFiles.map((file, index) => (
              <div
                key={`${file.fileId}-${index}`}
                className="flex max-w-full items-center gap-2 rounded-full bg-gray-50 px-3 py-1.5 text-gray-700"
              >
                <span className="truncate">{file.fileName}</span>
                <button
                  type="button"
                  onClick={() =>
                    setUploadedFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))
                  }
                  className="text-gray-400 transition-colors hover:text-red-500"
                  aria-label={`移除 ${file.fileName}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {uploading && <span className="text-gray-500">上传中...</span>}
            {uploadError && <span className="text-red-500">{uploadError}</span>}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {chips.map((chip) => (
          <button
            key={chip}
            type="button"
            onClick={() => setQuery(chip)}
            className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm shadow-sm transition-colors hover:border-[#0F60FF] hover:text-[#0F60FF]"
          >
            {chip}
          </button>
        ))}
      </div>
    </section>
  )
}

function AgentHub() {
  const agents = [
    {
      category: "教育场景",
      name: "全能备课助手",
      icon: BookOpenCheck,
      desc: "基于教材知识库，智能生成教案与课件大纲",
      bgGradient: "from-blue-600 to-blue-400",
      shadowColor: "shadow-blue-500/30",
      externalLink: "https://huizhi.wenkoo.com.cn/chat/?agentId=206812083621584896",
    },
    {
      category: "教育场景",
      name: "智能作文批改",
      icon: PenTool,
      desc: "多维度评分与详细的修改建议",
      bgGradient: "from-indigo-600 to-indigo-400",
      shadowColor: "shadow-indigo-500/30",
      externalLink: "https://huizhi.wenkoo.com.cn/chat/modify?agentId=1963836717443510273",
    },
    {
      category: "教育场景",
      name: "AI数理推演引擎",
      icon: SquareFunction,
      desc: "上传或输入数学或物理题目，提供详细的解题步骤，帮助学生掌握解题方法核步骤",
      bgGradient: "from-amber-600 to-amber-400",
      shadowColor: "shadow-amber-500/30",
      externalLink: "https://huizhi.wenkoo.com.cn/chat/?agentId=1920049629359443970",
    },
    {
      category: "能源场景",
      name: "采油工艺培训助手",
      icon: Factory,
      desc: "学习采油现场中的工艺原理、设备操作、异常诊断、安全规范与案例复盘。",
      bgGradient: "from-orange-600 to-orange-400",
      shadowColor: "shadow-orange-500/30",
      comingSoon: true,
    },
  ]

  return (
    <section className="mx-auto max-w-7xl px-4 py-12">
      <div className="mb-10 grid items-center gap-4 md:grid-cols-[1fr_auto_1fr]">
        <div className="hidden md:block" />
        <h2 className="text-center text-2xl font-bold">示范性应用场景</h2>
        <button
          type="button"
          onClick={() => window.open(`${window.location.origin}/chat/agent`, "_blank")}
          className="flex cursor-pointer items-center justify-self-center text-sm text-[#909399] transition-colors hover:text-[#303133] md:justify-self-end"
        >
          更多智能体
          <span className="ml-0.5 inline-flex h-3 w-3 items-center justify-center">
            <svg viewBox="0 0 1024 1024" aria-hidden="true">
              <path
                d="M769.216 511.936c0-3.712-1.152-7.232-1.856-10.88-0.64-2.368-0.64-4.8-1.408-7.04a50.56 50.56 0 0 0-11.136-17.344l-417.28-417.28a48.96 48.96 0 0 0-68.992-0.96 48.832 48.832 0 0 0 1.024 68.864L654.208 512l-384.64 384.768a48.768 48.768 0 0 0-1.024 68.8 48.768 48.768 0 0 0 68.864-0.96l417.28-417.344a51.2 51.2 0 0 0 11.136-17.344c0.896-2.304 0.896-4.736 1.472-7.04 0.768-3.648 1.92-7.232 1.92-10.944z"
                fill="currentColor"
              />
            </svg>
          </span>
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {agents.map((agent) => {
          const Icon = agent.icon
          const isComingSoon = agent.comingSoon
          return (
            <button
              key={agent.name}
              type="button"
              disabled={isComingSoon}
              onClick={agent.externalLink ? () => window.open(agent.externalLink, "_blank") : undefined}
              className={`group relative flex h-[320px] flex-col justify-between overflow-hidden rounded-[24px] p-6 text-left transition-all duration-300 ${isComingSoon
                ? "cursor-default bg-[#E4E7ED] text-[#606266] shadow-none"
                : `cursor-pointer bg-gradient-to-br ${agent.bgGradient} text-white shadow-xl ${agent.shadowColor} hover:-translate-y-2 hover:shadow-2xl`
                }`}
            >
              <div className={`absolute right-0 top-0 translate-x-4 -translate-y-4 p-4 transition-all duration-500 ${isComingSoon ? "opacity-10" : "opacity-10 group-hover:scale-110 group-hover:opacity-20"}`}>
                <Icon size={120} />
              </div>

              <div>
                <span className={`mb-4 inline-block rounded-full px-3 py-1 text-xs backdrop-blur-md ${isComingSoon ? "bg-black/5 text-[#606266]" : "bg-white/20 text-white"}`}>
                  {agent.category}
                </span>
                <Icon size={40} className="mb-6" />
                <h3 className="mb-2 text-2xl font-bold">{agent.name}</h3>
                <p className={`font-serif text-sm leading-relaxed ${isComingSoon ? "text-[#606266]" : "text-white/90"}`}>
                  {agent.desc}
                </p>
              </div>

              <div className={`mt-4 flex items-center justify-between border-t pt-4 ${isComingSoon ? "border-[#C0C4CC]" : "border-white/20"}`}>
                <span className="text-sm font-bold">{isComingSoon ? "即将上线" : "立即体验"}</span>
                {!isComingSoon && (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 transition-colors group-hover:bg-white group-hover:text-gray-900">
                    <ArrowRight size={16} />
                  </span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </section>
  )
}

function BookZone() {
  const books = [
    {
      title: "零碳科技",
      cover: `${ASSET_BASE}/1shu1ai/zero-carbon-tech.jpg`,
    },
    {
      title: "跟着古诗学写作",
      cover: `${ASSET_BASE}/1shu1ai/ancient-poetry-writing.jpg`,
    },
    {
      title: "儿童百科全书",
      cover: `${ASSET_BASE}/1shu1ai/children-encyclopedia.jpg`,
    },
    {
      title: "名著课程化丛书",
      cover: `${ASSET_BASE}/1shu1ai/classic-course-series.jpg`,
    },
  ]

  return (
    <section className="mx-auto mb-20 max-w-7xl px-4 py-16">
      <div className="mb-12 text-center">
        <h2 className="mb-2 text-2xl font-bold">一书一AI服务</h2>
        <p className="text-sm text-gray-500">
          扫描二维码，体验移动端专属知识服务
        </p>
      </div>

      <div className="grid grid-cols-1 items-start gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {books.map((book) => (
          <div
            key={book.title}
            className="group cursor-pointer transition-all duration-300 hover:-translate-y-2"
          >
            <img
              src={book.cover}
              alt={book.title}
              className="block h-auto w-full rounded-lg transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        ))}
      </div>
    </section>
  )
}

export default function Huizhi({
  data,
}: {
  data: IData
  lang: string
  bus: IBus
  templateId: string | number
}) {
  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#faf8f3] font-sans text-[20px] leading-[1.8] text-[#141413]">
      <div className="pointer-events-none absolute left-0 top-0 -z-10 h-[500px] w-full bg-gradient-to-b from-blue-50/50 to-transparent" />
      <div className="pointer-events-none absolute right-[-10%] top-[-20%] -z-10 h-[600px] w-[600px] rounded-full bg-blue-100/30 blur-3xl" />

      <HuizhiHeader />
      <main className="flex-1 overflow-y-auto">
        <SearchBox isExpired={data.expiredFlag} />
        <AgentHub />
        <BookZone />
      </main>
      <footer className="w-full px-4 pb-6 text-center text-xs text-gray-400">
        国家重点研发计划 基于生成式人工智能的专业知识服务关键技术研发与应用
      </footer>
    </div>
  )
}
